import { useInstanceStore } from "../stores/UseInstanceStore";
import { getAccess } from "../config/access";

let interceptorInstalled = false;

export function setupFetchAuthInterceptor() {
  if (interceptorInstalled) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  let refreshPromise = null;

  const persistTokens = (payload) => {
    if (!payload) return;
    const state = useInstanceStore.getState();
    if (payload.access_token && state.setJwtToken) {
      state.setJwtToken(payload.access_token);
    }
    if (Object.prototype.hasOwnProperty.call(payload, "refresh_token") && state.setRefreshToken) {
      state.setRefreshToken(payload.refresh_token || null);
    }
  };

  const clearTokens = () => {
    const state = useInstanceStore.getState();
    if (state.clearAuthTokens) {
      state.clearAuthTokens();
      return;
    }
    state.clearJwtToken?.();
    state.clearRefreshToken?.();
  };

  const resolveUrl = (input) => {
    if (input instanceof Request) return input.url;
    if (typeof input === "string") {
      try {
        return new URL(input, window.location.origin).toString();
      } catch {
        return window.location.href;
      }
    }
    if (input instanceof URL) {
      return input.toString();
    }
    return window.location.href;
  };

  const shouldCaptureAuthTokens = (url) => {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const segments = path.split("/").filter(Boolean);
      const last = segments[segments.length - 1];
      return last === "refresh";
    } catch {
      return false;
    }
  };

  const maybeCaptureTokens = async (response, url) => {
    if (!response || !shouldCaptureAuthTokens(url)) return;
    try {
      const data = await response.json();
      persistTokens(data);
    } catch (err) {
      console.warn("Failed to parse auth response", err);
    }
  };

  const prepareFetchArgs = (input, init, token, forceToken = false) => {
    const resolvedUrl = resolveUrl(input);
    if (input instanceof Request) {
      if (!token) {
        return { input, init, url: resolvedUrl };
      }
      const headers = new Headers(input.headers || {});
      if (forceToken || !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
        return { input: new Request(input, { headers }), init, url: resolvedUrl };
      }
      return { input, init, url: resolvedUrl };
    }
    const headers = new Headers(init?.headers || {});
    if (token && (forceToken || !headers.has("Authorization"))) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const baseInit = init ? { ...init } : {};
    baseInit.headers = headers;
    return {
      input,
      init: baseInit,
      url: resolvedUrl,
    };
  };

  const buildRefreshUrl = (failingUrl) => {
    const apiBaseUrl = useInstanceStore.getState().instance?.apiBaseUrl;
    if (apiBaseUrl) {
      const normalized = apiBaseUrl.replace(/\/+$/, "");
      return `${normalized}/refresh`;
    }
    try {
      const parsed = new URL(failingUrl, window.location.origin);
      return `${parsed.origin}/refresh`;
    } catch {
      return "/refresh";
    }
  };

  const requestTokenRefresh = async (failingUrl) => {
    // Sessions are closed (KAT-230) — a session must not renew itself past the
    // cut. Without this, a token that survives the purge keeps refreshing.
    if (!getAccess().restoreSession) {
      console.log("[Auth] Sessions are closed, not refreshing");
      clearTokens();
      dispatchSessionExpired("Sessions are closed");
      return false;
    }

    const refreshToken = useInstanceStore.getState().instance?.refreshToken;
    if (!refreshToken) {
      console.warn("[Auth] No refresh token available, cannot refresh session");
      clearTokens();
      dispatchSessionExpired("No refresh token available");
      return false;
    }

    if (!refreshPromise) {
      const refreshUrl = buildRefreshUrl(failingUrl);
      console.log("[Auth] Attempting token refresh...");
      
      refreshPromise = originalFetch(refreshUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
        .then(async (resp) => {
          if (!resp.ok) {
            const errorText = await resp.text().catch(() => "Unknown error");
            console.error(`[Auth] Refresh failed with status ${resp.status}: ${errorText}`);
            throw new Error(`Refresh failed: ${errorText}`);
          }
          const data = await resp.json();
          console.log("[Auth] Token refresh successful");
          persistTokens(data);
          return true;
        })
        .catch((err) => {
          console.error("[Auth] Token refresh request failed:", err.message);
          clearTokens();
          dispatchSessionExpired(err.message);
          return false;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    return refreshPromise;
  };

  // Dispatch an event when the session expires so the UI can respond
  const dispatchSessionExpired = (reason) => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("sessionExpired", { 
        detail: { reason, timestamp: Date.now() } 
      }));
    }
  };

  window.fetch = async (input, init = {}) => {
    const token = useInstanceStore.getState().instance?.jwtToken;
    const firstAttempt = prepareFetchArgs(input, init, token);
    let response = await originalFetch(firstAttempt.input, firstAttempt.init);
    await maybeCaptureTokens(response.clone(), firstAttempt.url);

    if (response.status !== 401 || shouldCaptureAuthTokens(firstAttempt.url)) {
      return response;
    }

    const refreshed = await requestTokenRefresh(firstAttempt.url);
    if (!refreshed) {
      return response;
    }

    const newToken = useInstanceStore.getState().instance?.jwtToken;
    const retryAttempt = prepareFetchArgs(input, init, newToken, true);
    const retryResponse = await originalFetch(retryAttempt.input, retryAttempt.init);
    await maybeCaptureTokens(retryResponse.clone(), retryAttempt.url);
    return retryResponse;
  };

  interceptorInstalled = true;
}
